import Phaser from 'phaser';
import { audio } from '../game/AudioManager.js';
import { dialogueManager } from '../game/DialogueManager.js';
import { questManager } from '../game/QuestManager.js';
import { gameState } from '../game/GameState.js';
import { DIALOGUES } from '../data/dialogue.js';
import { promptManager } from '../game/UIManager.js';
import { TouchControls } from '../game/TouchControls.js';

export class Level1Scene extends Phaser.Scene {
  constructor() {
    super('Level1Scene');
    this.stage = 1;
    this.player = null;
    this.talha = null;
    this.cursors = null;
    this.wasd = null;
    this.canInteract = false;
    this.interactPrompt = null;
    this.streetNpcs = [];
    this.touchControls = null;
    this.touchMoveX = 0;
    this.touchMoveY = 0;
  }

  create() {
    this.stage = 1;
    gameState.currentChapter = 1;
    questManager.show(true);
    questManager.setQuest("The School Road", "Explore the path toward home", "CHAPTER 1 • 2011");
    audio.playTheme('school');

    const { width, height } = this.scale;

    this.createStreetEnvironment();

    // Player Young Emily (KID LABONNO) - scale 0.38 matches student NPCs (~90px height)
    this.player = this.physics.add.sprite(150, 440, 'kid_emily_sheet')
      .setScale(0.38)
      .setOrigin(0.5, 0.95)
      .play('kid_emily_idle');
    this.player.setCollideWorldBounds(true);
    this.player.body.setSize(60, 36).setOffset(98, 208);

    // Young Talha (KID TALHA) walking ahead
    this.talha = this.physics.add.sprite(480, 440, 'kid_talha_sheet')
      .setScale(0.38)
      .setOrigin(0.5, 0.95)
      .play('kid_talha_walk');
    this.talha.body.setSize(60, 36).setOffset(98, 208);

    // Talha walks ahead toward apartment corner
    this.tweens.add({
      targets: this.talha,
      x: 980,
      duration: 3800,
      onComplete: () => {
        this.talha.play('kid_talha_idle');
        dialogueManager.start(DIALOGUES.level1_notice, () => {
          this.tweens.add({
            targets: this.talha,
            x: 1350,
            alpha: 0,
            duration: 2000,
            onComplete: () => {
              questManager.setQuest("Walk down the road and climb the apartment stairs to the Rooftop (Go Right)");
            }
          });
        });
      }
    });

    this.cameras.main.setBounds(0, 0, 1672, 540);
    this.physics.world.setBounds(0, 0, 1672, 540);
    this.cameras.main.startFollow(this.player, true, 0.08, 0.08);

    this.cursors = this.input.keyboard.createCursorKeys();
    this.wasd = this.input.keyboard.addKeys({
      up: Phaser.Input.Keyboard.KeyCodes.W,
      down: Phaser.Input.Keyboard.KeyCodes.S,
      left: Phaser.Input.Keyboard.KeyCodes.A,
      right: Phaser.Input.Keyboard.KeyCodes.D,
      interact: Phaser.Input.Keyboard.KeyCodes.E,
      jump: Phaser.Input.Keyboard.KeyCodes.SPACE
    });

    // Entrance to apartment building stairs at right end of the road
    this.doorZone = this.add.zone(1520, 440, 100, 160);
    this.physics.world.enable(this.doorZone, 0);

    // Initialize Universal Touch Controls (Adventure mode)
    this.touchControls = new TouchControls(this, 'adventure', {
      onMove: (x, y) => {
        this.touchMoveX = x;
        this.touchMoveY = y;
      },
      onJump: () => {
        this.handleJump();
      },
      onInteract: () => {
        this.handleInteract();
      }
    });
    this.touchControls.mount();

    this.events.on('shutdown', () => {
      if (this.touchControls) {
        this.touchControls.destroy();
        this.touchControls = null;
      }
    });
  }

  createStreetEnvironment() {
    // 1. High-fidelity Pixel Art School Road Background
    this.bgRoad = this.add.image(0, 0, 'bg_school_road')
      .setOrigin(0, 0)
      .setDisplaySize(1672, 540);

    // Subtle warm golden sunlight overlay
    const sunGlow = this.add.graphics();
    sunGlow.fillGradientStyle(0xffd166, 0xff70a6, 0xffd166, 0x000000, 0.12);
    sunGlow.fillRect(0, 0, 1672, 540);
    sunGlow.setDepth(1);

    // Floating golden dust particles
    this.add.particles(0, 0, 'petal_particle', {
      x: { min: 0, max: 1672 },
      y: { min: 0, max: 540 },
      lifespan: 5000,
      speedY: { min: -15, max: 15 },
      speedX: { min: 10, max: 35 },
      scale: { start: 0.6, end: 0.2 },
      alpha: { start: 0.4, end: 0.05 },
      quantity: 1,
      frequency: 300
    }).setDepth(2);

    // 2. Populate Authentic NPCs along the street with live interactive greetings
    // Spaced generously so no two strangers crowd each other
    const npcs = [
      { key: 'npc_schoolboy_0', x: 240, y: 440, flip: false, text: "MUBC Student", greeting: "Hey Labonno! Heading home?", icon: "🎒" },
      { key: 'npc_schoolgirl_0', x: 420, y: 435, flip: true, text: "School Friend", greeting: "Labonno! Did you see the notice board?", icon: "🌸" },
      { key: 'npc_ped_male_2', x: 640, y: 430, flip: false, text: "Local Resident", greeting: "Careful crossing the road, maa!", icon: "✨" },
      { key: 'npc_schoolboy_2', x: 840, y: 445, flip: false, text: "Classmate", greeting: "The school bell is going to ring!", icon: "⏰" },
      { key: 'npc_ped_female_2', x: 1020, y: 435, flip: true, text: "Neighborhood Auntie", greeting: "Send my salam to your mother!", icon: "🍬" },
      { key: 'npc_ped_male_0', x: 1180, y: 440, flip: true, text: "Passerby", greeting: "Lovely afternoon for a walk.", icon: "🍃" },
      { key: 'npc_ped_female_0', x: 1320, y: 435, flip: false, text: "Resident", greeting: "Good day, little one! Stay safe!", icon: "🌻" },
      { key: 'npc_schoolgirl_1', x: 1460, y: 440, flip: true, text: "Student", greeting: "See you at the rooftop later!", icon: "👋" }
    ];

    this.streetNpcs = [];

    npcs.forEach(npcData => {
      const sprite = this.add.image(npcData.x, npcData.y, npcData.key)
        .setScale(0.48)
        .setOrigin(0.5, 1)
        .setFlipX(npcData.flip)
        .setDepth(5);

      // Subtle breathing / idle motion
      this.tweens.add({
        targets: sprite,
        y: npcData.y - 3,
        duration: 1200 + Math.random() * 600,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut'
      });

      // Interactive pop-up speech bubble container
      const bubble = this.add.container(npcData.x, npcData.y - 110).setDepth(20);
      bubble.setAlpha(0).setScale(0.3);

      const roleBadge = this.add.text(0, -14, `${npcData.icon} ${npcData.text}`, {
        fontFamily: 'Trebuchet MS, Arial, sans-serif',
        fontSize: '11px',
        color: '#ffd166',
        backgroundColor: '#1b0d2a',
        padding: { x: 6, y: 2 }
      }).setOrigin(0.5);

      const msgText = this.add.text(0, 10, npcData.greeting, {
        fontFamily: 'Georgia, serif',
        fontSize: '12px',
        color: '#fdf2f8',
        backgroundColor: 'rgba(27, 13, 42, 0.92)',
        padding: { x: 8, y: 4 }
      }).setOrigin(0.5);

      const maxW = Math.max(msgText.width, roleBadge.width) + 16;
      const box = this.add.rectangle(0, 0, maxW, 48)
        .setStrokeStyle(1.5, 0xff70a6);

      bubble.add([box, roleBadge, msgText]);

      this.streetNpcs.push({
        sprite,
        bubble,
        defaultFlip: npcData.flip,
        x: npcData.x,
        y: npcData.y,
        active: false
      });
    });

    // Street markers
    this.add.text(140, 200, "MUBC School Gate", {
      fontSize: '15px',
      color: '#fff',
      backgroundColor: 'rgba(0,0,0,0.6)',
      padding: { x: 6, y: 3 }
    }).setOrigin(0.5).setDepth(10);

    this.add.text(1520, 210, "Apartment Entrance", {
      fontSize: '15px',
      color: '#ffd166',
      backgroundColor: 'rgba(0,0,0,0.7)',
      padding: { x: 6, y: 3 }
    }).setOrigin(0.5).setDepth(10);
  }

  switchToRooftop() {
    this.stage = 2;
    audio.playRomanticChime();
    audio.playRooftopSceneAudio();
    this.cameras.main.fade(600, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.cameras.main.fadeIn(600);
      this.children.removeAll();

      const { width, height } = this.scale;

      // Real Sunset Rooftop Background
      this.bgRooftop = this.add.image(0, 0, 'bg_rooftop_sunset')
        .setOrigin(0, 0)
        .setDisplaySize(width, height)
        .setDepth(0);

      // Golden hour warm sunset glow
      const sunsetGlow = this.add.graphics();
      sunsetGlow.fillGradientStyle(0xff758c, 0xff7eb3, 0xffd166, 0x000000, 0.18);
      sunsetGlow.fillRect(0, 0, width, height);
      sunsetGlow.setDepth(1);

      // Gentle floating rose petals / evening breeze particles
      this.add.particles(0, 0, 'petal_particle', {
        x: { min: 0, max: width },
        y: { min: 0, max: height },
        lifespan: 5000,
        speedY: { min: 10, max: 30 },
        speedX: { min: 15, max: 40 },
        scale: { start: 0.8, end: 0.3 },
        alpha: { start: 0.5, end: 0.1 },
        quantity: 1,
        frequency: 240
      }).setDepth(2);

      // Reset camera bounds for rooftop single screen
      this.cameras.main.setBounds(0, 0, width, height);
      this.physics.world.setBounds(0, 0, width, height);

      // Young Emily (KID LABONNO)
      this.player = this.physics.add.sprite(240, height - 80, 'kid_emily_sheet')
        .setScale(0.38)
        .setOrigin(0.5, 0.95)
        .setDepth(10)
        .play('kid_emily_idle');
      this.player.setCollideWorldBounds(true);
      this.player.body.setSize(60, 36).setOffset(98, 208);

      // Young Talha (KID TALHA) facing away toward railing at sunset
      // Frame 0: Facing away into the distance
      this.talha = this.physics.add.sprite(640, height - 80, 'kid_talha_sheet')
        .setScale(0.38)
        .setOrigin(0.5, 0.95)
        .setDepth(10)
        .setFrame(0);
      this.talha.setFlipX(false);

      questManager.setQuest("Approach the boy on the rooftop");
      if (this.touchControls) {
        this.touchControls.setInteractHighlight(false, 'Talk');
      }
    });
  }

  handleJump() {
    if (!this.player || !this.player.body || this.isJumping) return;
    this.isJumping = true;
    audio.playClick();
    this.player.play('kid_emily_jump');
    const startY = this.player.y;
    this.tweens.add({
      targets: this.player,
      y: startY - 45,
      duration: 220,
      yoyo: true,
      ease: 'Power1',
      onComplete: () => {
        this.isJumping = false;
        if (this.player && this.player.active) {
          this.player.y = startY;
        }
      }
    });
  }

  handleInteract() {
    if (!this.player || !this.player.body) return;

    if (this.stage === 1) {
      if (this.doorZone && Phaser.Geom.Intersects.RectangleToRectangle(this.player.getBounds(), this.doorZone.getBounds())) {
        promptManager.hide();
        if (this.touchControls) this.touchControls.setInteractHighlight(false);
        this.switchToRooftop();
      }
    } else if (this.stage === 2) {
      if (this.talha) {
        const dist = Phaser.Math.Distance.Between(this.player.x, this.player.y, this.talha.x, this.talha.y);
        if (dist < 120) {
          promptManager.hide();
          if (this.touchControls) this.touchControls.setInteractHighlight(false);
          this.player.setVelocity(0, 0);
          this.player.play('kid_emily_idle');

          dialogueManager.start(DIALOGUES.level1_rooftop, () => {
            audio.stopRooftopSceneAudio();
            this.cameras.main.fade(1200, 0, 0, 0);
            this.cameras.main.once('camerafadeoutcomplete', () => {
              this.scene.start('Level2Scene');
            });
          });
        }
      }
    }
  }

  update() {
    if (!this.player || !this.player.body) return;

    // Pause player movement while dialogue box is open
    const dlg = document.getElementById('dialogue');
    if (dlg && !dlg.classList.contains('hidden')) {
      this.player.setVelocity(0, 0);
      if (this.player.anims && this.player.anims.currentAnim?.key !== 'kid_emily_idle') {
        this.player.play('kid_emily_idle');
      }
      return;
    }

    const speed = 180;
    let vx = 0;
    let vy = 0;

    const wantsJump = Phaser.Input.Keyboard.JustDown(this.wasd.jump) ||
      (this.stage === 2 && Phaser.Input.Keyboard.JustDown(this.wasd.up));

    if (wantsJump) {
      this.handleJump();
    }

    const keyLeft = this.cursors.left.isDown || this.wasd.left.isDown;
    const keyRight = this.cursors.right.isDown || this.wasd.right.isDown;
    const touchX = this.touchMoveX || 0;
    const wantsLeft = keyLeft || touchX < -0.15;
    const wantsRight = keyRight || touchX > 0.15;

    if (!this.isJumping) {
      if (wantsLeft) {
        const speedScale = keyLeft ? 1 : Math.min(1, Math.max(0.45, Math.abs(touchX)));
        vx = -speed * speedScale;
        this.player.setFlipX(true);
        if (this.player.anims.currentAnim?.key !== 'kid_emily_walk') this.player.play('kid_emily_walk');
      } else if (wantsRight) {
        const speedScale = keyRight ? 1 : Math.min(1, Math.max(0.45, Math.abs(touchX)));
        vx = speed * speedScale;
        this.player.setFlipX(false);
        if (this.player.anims.currentAnim?.key !== 'kid_emily_walk') this.player.play('kid_emily_walk');
      } else {
        if (this.player.anims.currentAnim?.key !== 'kid_emily_idle') this.player.play('kid_emily_idle');
      }
    } else {
      if (wantsLeft) {
        vx = -speed * 1.1;
        this.player.setFlipX(true);
      } else if (wantsRight) {
        vx = speed * 1.1;
        this.player.setFlipX(false);
      }
    }

    if (this.stage === 1) {
      const keyUp = this.cursors.up.isDown || this.wasd.up.isDown;
      const keyDown = this.cursors.down.isDown || this.wasd.down.isDown;
      const touchY = this.touchMoveY || 0;
      const wantsUp = keyUp || touchY < -0.15;
      const wantsDown = keyDown || touchY > 0.15;
      if (wantsUp) {
        const speedScale = keyUp ? 1 : Math.min(1, Math.max(0.45, Math.abs(touchY)));
        vy = -speed * 0.7 * speedScale;
      }
      if (wantsDown) {
        const speedScale = keyDown ? 1 : Math.min(1, Math.max(0.45, Math.abs(touchY)));
        vy = speed * 0.7 * speedScale;
      }
    }

    this.player.setVelocity(vx, vy);

    // Live street NPC interaction: STRICT SINGLE-SPEAKER MUTUAL EXCLUSION
    if (this.stage === 1 && this.streetNpcs && this.player) {
      let closestNpc = null;
      let minDistance = 80; // Trigger radius

      this.streetNpcs.forEach(npc => {
        const dist = Math.abs(this.player.x - npc.x);
        if (dist < minDistance) {
          minDistance = dist;
          closestNpc = npc;
        }
      });

      this.streetNpcs.forEach(npc => {
        if (npc === closestNpc) {
          npc.sprite.setFlipX(this.player.x < npc.x);
          if (!npc.active) {
            npc.active = true;
            this.tweens.add({
              targets: npc.bubble,
              alpha: 1,
              scale: 1,
              duration: 200,
              ease: 'Back.easeOut'
            });
            this.tweens.add({
              targets: npc.sprite,
              scaleY: 0.52,
              duration: 120,
              yoyo: true
            });
          }
        } else if (npc.active) {
          npc.active = false;
          npc.sprite.setFlipX(npc.defaultFlip);
          this.tweens.add({
            targets: npc.bubble,
            alpha: 0,
            scale: 0.3,
            duration: 160,
            ease: 'Power1'
          });
        }
      });
    }

    if (this.stage === 1) {
      if (Phaser.Geom.Intersects.RectangleToRectangle(this.player.getBounds(), this.doorZone.getBounds())) {
        promptManager.show('E', 'Climb Stairs to Rooftop');
        if (this.touchControls) this.touchControls.setInteractHighlight(true, 'Climb Stairs');
        if (Phaser.Input.Keyboard.JustDown(this.wasd.interact)) {
          this.handleInteract();
        }
      } else {
        promptManager.hide();
        if (this.touchControls) this.touchControls.setInteractHighlight(false, 'Interact');
      }
    } else if (this.stage === 2) {
      const dist = Phaser.Math.Distance.Between(this.player.x, this.player.y, this.talha.x, this.talha.y);
      if (dist < 120) {
        promptManager.show('E', 'Talk to Talha');
        if (this.touchControls) this.touchControls.setInteractHighlight(true, 'Talk to Talha');
        if (Phaser.Input.Keyboard.JustDown(this.wasd.interact)) {
          this.handleInteract();
        }
      } else {
        promptManager.hide();
        if (this.touchControls) this.touchControls.setInteractHighlight(false, 'Talk');
      }
    }
  }
}
